import React from 'react';
import { Layers } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика платежей
 */
const PaymentSchedule = ({ schedule, results, showFullSchedule, setShowFullSchedule }) => {
  if (!schedule || schedule.length === 0 || !results) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          График платежей ({results.paybackPeriodMonths} мес.)
        </h3>
        {/* Кнопка переключения только если график больше, чем предпросмотр */}
        {schedule.length > results.scheduleShort.length && (
          <Button
            size="sm"
            onClick={() => setShowFullSchedule(prev => !prev)}
            icon={<Layers className="w-4 h-4" />}
            variant="outline"
            aria-expanded={showFullSchedule}
          >
            {showFullSchedule ? 'Скрыть полный график' : `Показать все ${schedule.length} мес.`}
          </Button>
        )}
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[600px] relative payment-schedule-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
        <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold text-center">Мес.</th>
              <th scope="col" className="px-3 py-2 font-semibold">Дата</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">Общий платеж</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">Осн. долг</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">Проценты</th>
              {/* Условные заголовки */}
              {results.totalInsurance > 0 && <th scope="col" className="px-3 py-2 font-semibold text-right">Страх.</th>}
              {results.totalOtherCosts > 0 && schedule.some(i => i.otherCostsPayment > 0) && 
                <th scope="col" className="px-3 py-2 font-semibold text-right">Прочие</th>}
              {results.totalEarlyRepayment > 0 && 
                <th scope="col" className="px-3 py-2 font-semibold text-right">Досрочно</th>}
              <th scope="col" className="px-3 py-2 font-semibold text-right">Остаток долга</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">Ставка</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {/* Отображаем либо полный график, либо сокращенный предпросмотр */}
            {(showFullSchedule ? schedule : results.scheduleShort).map((item) => (
              <tr
                key={item.month}
                className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100
                  ${item.earlyRepayment > 0 ? 'bg-green-50 dark:bg-green-900/20' : ''}
                  ${item.isRefinanceMonth ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-300 dark:ring-blue-700' : ''}`}
              >
                <td className="px-3 py-1.5 whitespace-nowrap text-center">{item.month}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <time dateTime={item.date.toISOString().split('T')[0]}>
                    {item.date.toLocaleDateString('ru-RU', { month: 'short', year: 'numeric' })}
                  </time>
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">{formatCurrency(item.totalPayment, 0)}</td>
                <td className="px-3 py-1.5 whitespace-nowrap text-right">{formatCurrency(item.principalPayment, 0)}</td>
                <td className="px-3 py-1.5 whitespace-nowrap text-right text-red-600 dark:text-red-400">{formatCurrency(item.interestPayment, 0)}</td>
                {/* Условные ячейки */}
                {results.totalInsurance > 0 && 
                  <td className="px-3 py-1.5 whitespace-nowrap text-right text-xs text-gray-500 dark:text-gray-400">
                    {formatCurrency(item.insurancePayment, 0)}
                  </td>}
                {results.totalOtherCosts > 0 && schedule.some(i => i.otherCostsPayment > 0) && 
                  <td className="px-3 py-1.5 whitespace-nowrap text-right text-xs text-gray-500 dark:text-gray-400">
                    {item.otherCostsPayment > 0 ? formatCurrency(item.otherCostsPayment, 0) : '-'}
                  </td>}
                {results.totalEarlyRepayment > 0 && 
                  <td className={`px-3 py-1.5 whitespace-nowrap text-right ${item.earlyRepayment > 0 ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>
                    {item.earlyRepayment > 0 ? formatCurrency(item.earlyRepayment, 0) : '-'}
                  </td>}
                <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                  {formatCurrency(item.remainingBalance, 0)}
                </td>
                <td className={`px-3 py-1.5 whitespace-nowrap text-right text-xs ${item.isRefinanceMonth ? 'text-blue-600 dark:text-blue-400 font-semibold' : ''}`}>
                  {formatNumber(item.rate, 2)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Кнопка показа полного графика (если он скрыт и больше предпросмотра) */}
      {!showFullSchedule && schedule.length > results.scheduleShort.length && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowFullSchedule(true)}
            icon={<Layers className="w-4 h-4" />}
            variant="link"
          >
            Показать все {schedule.length} платежей
          </Button>
        </div>
      )}
    </div>
  );
};

export default PaymentSchedule;